import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { Button } from '@launchpad-ui/components';

import { ErrorMessage, PageHeader, StatusBadge } from '../components/ui';
import {
  useBulkDeleteQaAssignments,
  useDeleteQaAssignment,
  useQaAssignments,
  useSyncQaAssignments,
  useUpdateQaAssignment,
} from '../lib/hooks/useQaAssignments';
import { useReviewers } from '../lib/hooks/useReviewers';
import type { AssignmentStatus, QaAssignment } from '../lib/types';

import styles from '../styles/common.module.css';

function getWeekOf(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  const day = d.getUTCDay();
  const diff = day === 0 ? -6 : 1 - day;
  const monday = new Date(d);
  monday.setUTCDate(d.getUTCDate() + diff);
  return `Week of ${monday.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })}`;
}

export function QAQueue() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [selectedReviewer, setSelectedReviewer] = useState(searchParams.get('reviewer') ?? '');
  const [statusFilter, setStatusFilter] = useState<AssignmentStatus | 'all'>(
    (searchParams.get('status') as AssignmentStatus | 'all') ?? 'pending',
  );
  const [days, setDays] = useState(7);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  const reviewersQuery = useReviewers();
  const assignmentsQuery = useQaAssignments({
    reviewer: selectedReviewer || undefined,
    status: statusFilter,
  });
  const updateMutation = useUpdateQaAssignment();
  const deleteMutation = useDeleteQaAssignment();
  const bulkDeleteMutation = useBulkDeleteQaAssignments();
  const syncMutation = useSyncQaAssignments();

  const assignments = assignmentsQuery.data ?? [];
  const activeReviewers = (reviewersQuery.data ?? []).filter((r) => r.active);
  const pendingCount = assignments.filter((a) => a.status === 'pending').length;

  const handleSync = () => syncMutation.mutate(days);

  const allSelected = assignments.length > 0 && selectedIds.size === assignments.length;
  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(assignments.map((a) => a.id)));
    }
  };
  const toggleOne = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };
  const handleBulkDelete = () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    if (!window.confirm(`Remove ${ids.length} assignment${ids.length === 1 ? '' : 's'}?`)) return;
    bulkDeleteMutation.mutate(ids, {
      onSuccess: () => setSelectedIds(new Set()),
    });
  };

  const startReview = (a: QaAssignment) => {
    navigate(`/review?ticket=${a.ticket_id}&tse=${encodeURIComponent(a.tse_name)}&assignment=${a.id}`);
  };

  return (
    <div className={styles.page}>
      <PageHeader
        title="Ticket Review"
        subtitle={`${pendingCount} pending review${pendingCount === 1 ? '' : 's'}${
          selectedReviewer ? ` for ${selectedReviewer}` : ''
        }`}
        actions={
          <>
            {selectedIds.size > 0 && (
              <Button variant="destructive" onPress={handleBulkDelete} isDisabled={bulkDeleteMutation.isPending}>
                {bulkDeleteMutation.isPending ? 'Deleting…' : `Delete ${selectedIds.size} selected`}
              </Button>
            )}
            <select
              className={styles.select}
              style={{ width: 'auto' }}
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
            >
              <option value={7}>Last 7 days</option>
              <option value={14}>Last 14 days</option>
              <option value={30}>Last 30 days</option>
            </select>
            <Button onPress={handleSync} isDisabled={syncMutation.isPending}>
              {syncMutation.isPending ? 'Syncing…' : 'Sync from Zendesk'}
            </Button>
          </>
        }
      />

      {syncMutation.data && (
        <div className={styles.success}>
          Sync complete — <strong>{syncMutation.data.created}</strong> new assignment
          {syncMutation.data.created === 1 ? '' : 's'} created, <strong>{syncMutation.data.skipped}</strong> already
          existed, <strong>{syncMutation.data.unmatched}</strong> tickets had no matching new hire.
          {syncMutation.data.message && <> {syncMutation.data.message}</>}
        </div>
      )}
      {syncMutation.error && <ErrorMessage message={(syncMutation.error as Error).message} />}

      <div className={styles.row} style={{ marginBottom: '1rem' }}>
        <select
          className={styles.select}
          style={{ width: 'auto' }}
          value={selectedReviewer}
          onChange={(e) => setSelectedReviewer(e.target.value)}
        >
          <option value="">All Reviewers</option>
          {activeReviewers.map((r) => (
            <option key={r.id} value={r.name}>
              {r.name}
            </option>
          ))}
        </select>
        <div style={{ display: 'flex', gap: '0.25rem' }}>
          {(['pending', 'completed', 'all'] as const).map((s) => (
            <Button
              key={s}
              size="small"
              variant={statusFilter === s ? 'primary' : 'minimal'}
              onPress={() => setStatusFilter(s)}
            >
              {s}
            </Button>
          ))}
        </div>
      </div>

      {assignmentsQuery.isLoading ? (
        <div className={styles.empty}>Loading…</div>
      ) : assignments.length === 0 ? (
        <div className={styles.empty}>
          {statusFilter === 'pending'
            ? 'No pending assignments. Sync from Zendesk to generate the queue.'
            : 'No assignments match your filters.'}
        </div>
      ) : (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.th}>
                  <input
                    type="checkbox"
                    aria-label="Select all assignments"
                    checked={allSelected}
                    onChange={toggleSelectAll}
                  />
                </th>
                <th className={styles.th}>Ticket</th>
                <th className={styles.th}>Subject</th>
                <th className={styles.th}>New Hire</th>
                <th className={styles.th}>Reviewer</th>
                <th className={styles.th}>Date Solved</th>
                <th className={styles.th}>Status</th>
                <th className={styles.th} />
                <th className={styles.th}>Review Week</th>
              </tr>
            </thead>
            <tbody>
              {assignments.map((a) => (
                <tr key={a.id} className={a.status !== 'pending' ? styles.dimmed : ''}>
                  <td className={styles.td}>
                    <input
                      type="checkbox"
                      aria-label={`Select assignment ${a.id}`}
                      checked={selectedIds.has(a.id)}
                      onChange={() => toggleOne(a.id)}
                    />
                  </td>
                  <td className={styles.td}>
                    <a href={a.ticket_url} target="_blank" rel="noopener noreferrer">
                      #{a.ticket_id}
                    </a>
                  </td>
                  <td
                    className={styles.td}
                    style={{
                      maxWidth: '16rem',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    title={a.ticket_subject ?? ''}
                  >
                    {a.ticket_subject || '—'}
                  </td>
                  <td className={styles.td}>{a.tse_name}</td>
                  <td className={styles.td}>{a.assigned_reviewer}</td>
                  <td className={styles.td}>
                    {a.ticket_closed_at ? new Date(a.ticket_closed_at).toLocaleDateString() : '—'}
                  </td>
                  <td className={styles.td}>
                    <StatusBadge status={a.status} />
                  </td>
                  <td className={styles.td}>
                    <div className={styles.actionsCell}>
                      {a.status === 'pending' && (
                        <>
                          <Button size="small" onPress={() => startReview(a)}>
                            Start Review
                          </Button>
                          <Button
                            size="small"
                            variant="minimal"
                            onPress={() => updateMutation.mutate({ id: a.id, patch: { status: 'skipped' } })}
                          >
                            Skip
                          </Button>
                        </>
                      )}
                      {a.status === 'completed' && (
                        <Button
                          size="small"
                          variant="minimal"
                          onPress={() => updateMutation.mutate({ id: a.id, patch: { status: 'pending' } })}
                        >
                          Reopen
                        </Button>
                      )}
                      {a.status === 'skipped' && (
                        <Button
                          size="small"
                          variant="minimal"
                          onPress={() => updateMutation.mutate({ id: a.id, patch: { status: 'pending' } })}
                        >
                          Restore
                        </Button>
                      )}
                      <Button
                        size="small"
                        variant="minimal"
                        onPress={() => {
                          if (window.confirm('Remove this assignment?')) {
                            deleteMutation.mutate(a.id);
                          }
                        }}
                      >
                        Remove
                      </Button>
                    </div>
                  </td>
                  <td className={styles.td}>{getWeekOf(a.ticket_closed_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
